// A trace's workspace, wherever its board is drawn: over the page (the
// workspace overlay, TraceCanvas) or in place of the trace's lanes (the Lanes
// reading's session graph). Both hold the one board the trace keeps in
// lib/traceCanvasStore.js, so what is laid on it, sized, ordered, kept and
// tucked away there is the same in either place.
import { useCallback, useEffect, useMemo, useRef } from "react";
import WorkspaceAddPanel from "./WorkspaceAddOverlay.jsx";
import { useTraceNotes } from "../lib/traceNoteStore.js";
import {
  clearTraceCanvas, keepOnTraceCanvas, layOnTraceCanvas, putThingBack, reopenTraceCanvas, setTraceCanvasFollow, setTraceCanvasHidden, setTraceCanvasNote, setTraceCanvasNotes, setTraceCanvasOrder, setTraceCanvasReadings,
  setTraceCanvasSizes, takeOffTraceCanvas, takeThingOff, traceCanvasSnapshot, useTraceCanvas, useTraceCanvases, workspaceSessions,
} from "../lib/traceCanvasStore.js";

// How far the add sheet floats in from the board's edges.
export const SHEET_INSET = 10;

// What a board needs of its workspace (TraceLanesView's `canvas`): the traces
// laid on, what the reader did there, the notes on its traces, and the page's
// controls for it (`bar`). `canFollow` is whether the canvas has a chat whose
// investigation it can follow; `adding`, `onAdd` and `cover` are the add
// sheet's (WorkspaceAddSheet).
// `lead` and `tail` are the host's own cells for the board's bar, at
// its start and its end. `machines`: the machines the workspace holds, each
// with its sessions ({ id, name, person, sessions: [{ id, title, firstMs,
// lastMs, findings: [{ id, at, severity }] }] }), a card each on the board;
// `searches`, its searches with the sessions each matches, in the same shape;
// `nested`, the workspaces inside it, likewise (each with `sub`, what it
// holds). `onEnter` goes into one of those; `onExit` back out of this one;
// `flash` points a card out. `range`: the workspace's range, which the board
// reads how widely what its sessions share was touched across.
// `workspaceId`: the workspace the board shows; `inset`, what draws a
// workspace inside it in its card (SessionCanvasView's `nestInset`).
// It hands back, besides, what the workspace's walkthrough reads: `walkTraces`,
// every trace the workspace holds (its anchor first, then those laid on, then
// those inside the workspaces it holds, each with `inside` naming the one here
// holding it), and `walkChat`, the chat it is worked in ({ chatId, graphKey },
// lib/workspaceClaims.js), none until one is asked there.
// `dock`: whether the board keeps a dock (true), lends its tabs to the
// workspace home's (`"bridge"`, while it is the board in front: `dockFront`),
// or has none; `onCellPick`, where a board with no trace's reading sends a
// cell picked on it; `onCardSelect`, where any board sends the trace last
// selected on it (SessionCanvasView), for the home's dock to read. `empty`:
// what the board shows with nothing on it yet (in its bar's `empty`).
// `onClear` and `clearLabel`: what the bar's Clear does and says, in place of
// taking all but the anchor and the reader's notes off. `onClose`: closes the
// workspace where it is shown, from the board's top. `publish` false keeps
// the board from telling the page about itself (a preview over the panes,
// which leaves that to the board worked in).
export function useTraceWorkspaceHost(canvasId, { dock = false, dockFront = false, onCellPick = null, onCardSelect = null, empty = null, focus = null, onSelection = null, canFollow = false, adding = false, onAdd = null, cover = 0, lead = null, tail = null, machines = null, searches = null, nested = null, onEnter = null, onExit = null, flash = null, range = null, workspaceId = null, inset = null, onClear = null, clearLabel = null, onClose = null, publish = true } = {}) {
  const c = useTraceCanvas(canvasId);
  const onBoard = useMemo(() => [c.anchor, ...c.layered.map((t) => t.id)].filter(Boolean), [c.anchor, c.layered]);
  // One note written or moved (`nid`, `patch`), or several moved at once (a
  // list of { id, ...patch }).
  const onLooseNote = useCallback((nid, patch) => (Array.isArray(nid) ? setTraceCanvasNotes(canvasId, nid) : setTraceCanvasNote(canvasId, nid, patch)), [canvasId]);
  // A machine, a search or a note off the workspace (what it was comes back
  // for an Undo), and back on.
  const onThingOff = useCallback((kind, thing) => takeThingOff(canvasId, kind, thing), [canvasId]);
  const onThingBack = useCallback((kind, was) => putThingBack(canvasId, kind, was), [canvasId]);
  // Out from something on the board to what it belongs to, kept on it: a
  // session's machine, or a search for what a cell holds (a file). Says
  // which card that is on the board.
  const onBranch = useCallback((thing) => {
    if (thing?.kind === "machine") {
      keepOnTraceCanvas(canvasId, { machines: [{ id: thing.id, name: thing.name || "" }] });
      return { key: `graph:machine:${thing.id}` };
    }
    if (thing?.kind === "search" && thing.q) {
      keepOnTraceCanvas(canvasId, { searches: [{ q: thing.q, label: thing.label || thing.q }] });
      const kept = traceCanvasSnapshot(canvasId)?.searches?.find((q) => q.q === thing.q.trim());
      return kept ? { key: `graph:search:${kept.id}` } : null;
    }
    return null;
  }, [canvasId]);
  // A workspace holding more than one trace is walked through whole, the
  // sessions inside the workspaces it holds too: their notes are read with its
  // own, for the walkthrough's moments in them to stand on the card of the
  // workspace here each is in (by its id). The same while what it holds is.
  const tracesKey = JSON.stringify(workspaceSessions(canvasId).map((t) => [t.id, t.title, t.via, t.viaTitle]));
  const walkTraces = useMemo(() => JSON.parse(tracesKey).map(([id, title, via, inside]) => ({ id, title, via, inside })), [tracesKey]);
  const nestedHomes = useMemo(() => {
    const homes = new Map(walkTraces.filter((t) => t.via).map((t) => [t.id, t.via]));
    return homes.size ? homes : null;
  }, [walkTraces]);
  const walkChat = useMemo(() => {
    const graphKey = canvasId?.startsWith("chat:") ? canvasId.slice("chat:".length) : null;
    return graphKey || c.chatId != null ? { chatId: c.chatId ?? null, graphKey } : null;
  }, [canvasId, c.chatId]);
  const notes = useTraceNotes(nestedHomes?.size ? [...onBoard, ...nestedHomes.keys()] : onBoard);
  const bar = useMemo(() => ({
    canFollow, following: c.follow, onFollow: () => setTraceCanvasFollow(canvasId, !c.follow),
    onClear: (opts) => (onClear ? onClear(opts) : clearTraceCanvas(canvasId, opts)),
    clearLabel: clearLabel || (c.anchor ? "Take every other session, machine and search off this workspace; your notes stay" : "Take every session, machine, search and workspace off this one; your notes stay"),
    adding, onAdd, cover, lead, tail, empty, onClose,
    // A note of the reader's own, along the board's top band.
    onNote: () => keepOnTraceCanvas(canvasId, { looseNotes: [{ text: "" }] }),
  }), [canvasId, canFollow, c.follow, c.anchor, adding, onAdd, cover, lead, tail, empty, onClear, clearLabel, onClose]);
  const layered = useMemo(() => c.layered.map((t) => t.id), [c.layered]);
  // What the workspace calls its sessions, for their cards until their own
  // summaries are read.
  const titles = useMemo(() => {
    const out = {};
    for (const t of c.layered) if (t.title) out[t.id] = t.title;
    if (c.anchor && c.title && !out[c.anchor]) out[c.anchor] = c.title;
    return out;
  }, [c.layered, c.anchor, c.title]);
  return useMemo(() => ({
    dock,
    dockFront,
    onCellPick,
    onCardSelect,
    onSelection,
    focus,
    layered,
    titles,
    onLayer: (items) => layOnTraceCanvas(canvasId, items),
    onUnlayer: (sid) => takeOffTraceCanvas(canvasId, sid),
    onAnchor: null,
    readings: c.readings,
    onReadings: (v) => setTraceCanvasReadings(canvasId, v),
    hidden: c.hidden,
    onHidden: (v) => setTraceCanvasHidden(canvasId, v),
    notes,
    nestedHomes,
    walkTraces,
    walkChat,
    sizes: c.sizes,
    onSizes: (v) => setTraceCanvasSizes(canvasId, v),
    order: c.order,
    onOrder: (v) => setTraceCanvasOrder(canvasId, v),
    machines,
    searches,
    nested,
    onEnter,
    onExit,
    flash,
    range,
    workspaceId,
    inset,
    pinnedFindings: c.findings,
    looseNotes: c.looseNotes,
    onLooseNote,
    onThingOff,
    onThingBack,
    onBranch,
    bar,
    publish,
  }), [canvasId, dock, dockFront, onCellPick, onCardSelect, onSelection, focus, layered, titles, c.readings, c.hidden, notes, nestedHomes, walkTraces, walkChat, c.sizes, c.order, machines, searches, nested, onEnter, onExit, flash, range, workspaceId, inset, c.findings, c.looseNotes, onLooseNote, onThingOff, onThingBack, onBranch, bar, publish]);
}

// The picker for what to lay on a board, docked on the board's right side,
// reporting how much of the board it covers (`onCover`) so the board keeps
// what it shows clear of it.
export function WorkspaceAddSheet({ open, onClose, onCover, canvasId, range = "1M", onRangeChange = null, title = "" }) {
  const c = useTraceCanvas(canvasId);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!open || !el) { onCover?.(0); return undefined; }
    const measure = () => onCover?.(Math.round(el.getBoundingClientRect().width) + SHEET_INSET);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // A workspace of the reader's own has no anchor to list among what is on it.
  const traces = useMemo(() => [...(c.anchor ? [{ id: c.anchor, title: title || c.title }] : []), ...c.layered], [c.anchor, c.title, c.layered, title]);
  const canvases = useTraceCanvases();
  if (!open) return null;
  return (
    <div
      ref={ref}
      className="absolute z-20 flex w-[clamp(420px,46%,620px)] min-h-0 flex-col rounded-[2px] shadow-[var(--node-shadow-lift)]"
      style={{ top: SHEET_INSET, right: SHEET_INSET, bottom: SHEET_INSET }}
      data-trace-canvas-add=""
    >
      <WorkspaceAddPanel range={range} onRangeChange={onRangeChange} onClose={onClose} floating traces={traces} anchor={c.anchor} onLay={(entry) => layOnTraceCanvas(canvasId, entry)} chat={false} canvases={canvases} activeCanvas={canvasId} onOpenCanvas={reopenTraceCanvas} />
    </div>
  );
}
